import React, { useEffect, useState } from 'react';
import { alpha, Box, Popper, Stack } from '@mui/material';
import { TopicsInfoTopic } from 'api/editor/topics';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { renderHtml } from 'tools/safeHtml';
import { useTheme } from '@mui/material/styles';
import styles from './MentionGlossaryTermTooltip.styles';
import GlossaryTermStatus from 'components/common/glossary-term-status/GlossaryTermStatus';

interface MentionGlossaryTermTooltipProps {
    data?: 'LOADING' | 'REMOVED' | TopicsInfoTopic;
    anchor: any;
    onLeave: () => void;
}
export default function MentionGlossaryTermTooltip(props: MentionGlossaryTermTooltipProps) {
    const { t } = useBundleTranslation(['components/common/mention-glossary-term-tooltip']);
    const appTheme = useTheme();
    const { anchor, data, onLeave = () => {} } = props;
    const maxRowsContent = 4;

    const [definitionCut, setDefinitionCut] = useState<boolean>(false);
    const [definitionExpand, setDefinitionExpand] = useState<boolean>(false);
    const definitionContentRef = React.useRef<HTMLDivElement>(null);

    const [descriptionCut, setDescriptionCut] = useState<boolean>(false);
    const [descriptionExpand, setDescriptionExpand] = useState<boolean>(false);
    const descriptionContentRef = React.useRef<HTMLDivElement>(null);

    const maxContentContainerHeight =
        maxRowsContent * appTheme.size.lineHeight * (appTheme.typography.body1.fontSize as number);

    useEffect(() => {
        if (!anchor && !data) setDefinitionCut(false);
        setDefinitionExpand(false);
        setDescriptionCut(false);
        setDescriptionExpand(false);
    }, [anchor, data]);

    useEffect(() => {
        if (definitionContentRef.current && data && !(data == 'LOADING' || data == 'REMOVED')) {
            setDefinitionCut(definitionContentRef.current.clientHeight > maxContentContainerHeight);
        }
    }, [definitionContentRef.current, data]);

    useEffect(() => {
        if (descriptionContentRef.current && data && !(data == 'LOADING' || data == 'REMOVED')) {
            setDescriptionCut(descriptionContentRef.current.clientHeight > maxContentContainerHeight);
        }
    }, [descriptionContentRef.current, data]);

    if (!anchor || !data) return null;

    return (
        <Popper
            open={true}
            anchorEl={anchor}
            placement={'bottom-start'}
            className={'mention-item-term-tooltip'}
            sx={styles.popper}
        >
            <Stack
                onMouseLeave={() => {
                    onLeave();
                }}
                sx={{
                    ...styles.container,
                    width: data == 'LOADING' || data == 'REMOVED' ? 'auto' : '355px',
                }}
            >
                {data === 'LOADING' ? (
                    <Stack
                        direction={'row'}
                        spacing={1}
                        alignItems={'center'}
                        sx={{ color: (theme) => alpha(theme.palette.text.primary, 0.64) }}
                    >
                        <IconMi icon={'loader'} className={'anim-spin'} /> <Box>{t('loading___')}</Box>
                    </Stack>
                ) : (
                    <>
                        {data === 'REMOVED' ? (
                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                <IconMi icon={'alert'} sx={{ fontSize: '16px' }} />
                                <Box>{t('this_element_does_not_exist')}</Box>
                            </Stack>
                        ) : (
                            <>
                                <Stack direction={'row'} sx={styles.header}>
                                    <Box sx={styles.title}>{data.name}</Box>
                                    <GlossaryTermStatus status={data.status} />
                                </Stack>

                                <Stack spacing={1} sx={styles.content}>
                                    <Stack spacing={0.5}>
                                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                            <Box sx={styles.contentLabel}>{t('definition_label')}</Box>
                                            <Box sx={styles.contentLabelLine} />
                                        </Stack>
                                        <Box
                                            className={definitionCut && !definitionExpand ? 'active-cut' : ''}
                                            sx={{
                                                ...styles.cutContainer,
                                                maxHeight:
                                                    definitionCut && !definitionExpand
                                                        ? `${maxContentContainerHeight}px`
                                                        : 'none',
                                            }}
                                        >
                                            <Box ref={definitionContentRef}>
                                                {data.definition && data.definition > '' ? (
                                                    <Box className={'richtext-holder'}>
                                                        {renderHtml(data.definition ?? '')}
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            color: (theme) => alpha(theme.palette.text.primary, 0.24),
                                                        }}
                                                    >
                                                        {t('no_definition_text')}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        {definitionCut && !definitionExpand && (
                                            <Box
                                                onClick={() => {
                                                    setDefinitionExpand(true);
                                                }}
                                                sx={styles.moreLink}
                                            >
                                                {t('show_more_link')}
                                            </Box>
                                        )}
                                    </Stack>

                                    <Stack spacing={0.5}>
                                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                            <Box sx={styles.contentLabel}>{t('description_label')}</Box>
                                            <Box sx={styles.contentLabelLine} />
                                        </Stack>
                                        <Box
                                            className={descriptionCut && !descriptionExpand ? 'active-cut' : ''}
                                            sx={{
                                                ...styles.cutContainer,
                                                maxHeight:
                                                    descriptionCut && !descriptionExpand
                                                        ? `${maxContentContainerHeight}px`
                                                        : 'none',
                                            }}
                                        >
                                            <Box ref={descriptionContentRef}>
                                                {data.description && data.description > '' ? (
                                                    <Box className={'richtext-holder'}>
                                                        {renderHtml(data.description ?? '')}
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            color: (theme) => alpha(theme.palette.text.primary, 0.24),
                                                        }}
                                                    >
                                                        {t('no_description_text')}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        {descriptionCut && !descriptionExpand && (
                                            <Box
                                                onClick={() => {
                                                    setDescriptionExpand(true);
                                                }}
                                                sx={styles.moreLink}
                                            >
                                                {t('show_more_link')}
                                            </Box>
                                        )}
                                    </Stack>
                                </Stack>
                            </>
                        )}
                    </>
                )}
            </Stack>
        </Popper>
    );
}
